import { useEffect, useRef } from "react";

/**
 * Lightweight, original dotted-wave background for the SEM hero.
 * Uses only the Canvas 2D API; no third-party animation/component code.
 */
export function DottedSurfaceHero({
  className = "",
  opacity = 0.2,
  size = 1.45,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    const coarsePointer = window.matchMedia?.("(pointer: coarse)")?.matches;

    let cssWidth = 1;
    let cssHeight = 1;
    let dpr = 1;
    let raf = 0;
    let visible = true;
    let lastFrame = 0;
    const startedAt = performance.now();

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssWidth = Math.max(1, rect.width);
      cssHeight = Math.max(1, rect.height);
      dpr = Math.min(window.devicePixelRatio || 1, coarsePointer ? 1.15 : 1.5);
      const nextW = Math.max(1, Math.round(cssWidth * dpr));
      const nextH = Math.max(1, Math.round(cssHeight * dpr));
      if (canvas.width !== nextW || canvas.height !== nextH) {
        canvas.width = nextW;
        canvas.height = nextH;
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const drawFrame = (now) => {
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const t = reducedMotion ? 1.4 : (now - startedAt) / 1000;
      const cols = coarsePointer ? 24 : 38;
      const rows = coarsePointer ? 34 : 52;
      const horizon = cssHeight * 0.32;
      const nearY = cssHeight * 1.02;
      const centerX = cssWidth * 0.58;
      const span = cssWidth * 0.94;

      ctx.globalCompositeOperation = "lighter";

      for (let row = rows - 1; row >= 0; row -= 1) {
        const depth = row / Math.max(1, rows - 1);
        const perspective = 0.28 + Math.pow(depth, 1.12) * 0.92;
        const yBase = horizon + (nearY - horizon) * Math.pow(depth, 1.48);
        const rowAlpha = Math.pow(depth, 0.52);

        for (let col = 0; col < cols; col += 1) {
          const nx = col / Math.max(1, cols - 1) - 0.5;
          const waveA = Math.sin(col * 0.54 + t * 0.78);
          const waveB = Math.sin(row * 0.31 - t * 0.46);
          const waveC = Math.sin((col + row) * 0.17 + t * 0.34);
          const lift = (waveA * 0.52 + waveB * 0.32 + waveC * 0.16) * (10 + 22 * depth);

          const x = centerX + nx * span * perspective;
          const y = yBase - lift;

          if (x < -12 || x > cssWidth + 12 || y < -12 || y > cssHeight + 12) continue;

          const edgeFade = Math.max(0, 1 - Math.pow(Math.abs(nx) * 1.7, 2.4));
          const shimmer = 0.78 + 0.22 * Math.sin(col * 0.37 + row * 0.23 + t * 0.9);
          const alpha = opacity * rowAlpha * edgeFade * shimmer;
          if (alpha <= 0.006) continue;

          const radius = size * (0.55 + depth * 1.15) * (coarsePointer ? 0.9 : 1);
          ctx.beginPath();
          ctx.fillStyle = `rgba(0, 185, 232, ${alpha.toFixed(4)})`;
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      ctx.globalCompositeOperation = "source-over";
    };

    const animate = (now) => {
      raf = requestAnimationFrame(animate);
      if (!visible || document.hidden) return;
      const frameInterval = coarsePointer ? 44 : 34;
      if (now - lastFrame < frameInterval) return;
      lastFrame = now;
      drawFrame(now);
    };

    let observer = null;
    if ("IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          visible = entry.isIntersecting;
        },
        { threshold: 0.02 },
      );
    }

    const handleResize = () => {
      resize();
      if (reducedMotion) drawFrame(performance.now());
    };

    observer?.observe(canvas);
    window.addEventListener("resize", handleResize, { passive: true });
    resize();
    if (reducedMotion) {
      drawFrame(performance.now());
    } else {
      raf = requestAnimationFrame(animate);
    }

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [opacity, size]);

  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      style={{
        WebkitMaskImage:
          "linear-gradient(to bottom, transparent 10%, rgba(0,0,0,.08) 26%, #000 50%, #000 88%, transparent 100%)",
        maskImage:
          "linear-gradient(to bottom, transparent 10%, rgba(0,0,0,.08) 26%, #000 50%, #000 88%, transparent 100%)",
      }}
    >
      <canvas ref={canvasRef} className="h-full w-full" />
    </div>
  );
}
